import * as React from "react";
import { Fragment } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { AppBar, Toolbar, Grid, IconButton, Theme, Button } from "@material-ui/core";
import MenuIcon from "@material-ui/icons/Menu";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { withRouter } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import LeftHandMenu from "../shared/LeftHandMenu";
import { RootState } from "../../store/rootReducer";
import { ReactComponent as NavBarLogoSvg } from "../../assets/images/navBarLogo.svg";
import { IAppPropsWithRoute } from "../../common/types";
import { Action, ThunkDispatch } from "@reduxjs/toolkit";
import RightHandMenu from "./RightHandMenu";
import { consts } from "../../common/consts";

interface NavBarProps extends IAppPropsWithRoute {
  righButtonName?: string;
  humberMenu?: JSX.Element | null;
  rightButton?: JSX.Element | null;
  noLogo?: boolean;
  noNavBarShadow?: boolean;
}

interface IUserData {
  givenName: string;
  surName: string;
  email: string;
  role: string;
  IsAdmin: boolean;
  exp: number;
  location: string;
  ward: string;
}

interface IAuthState {
  isAuthenticated?: boolean;
  token: string | null;
  userName: string | null;
  data: IUserData | null;
  sessionExpired: boolean;
  vehicleSessionId: string | null;
  vehicleNumber: string | null;
}

interface IUserType {
  IsAdmin: boolean | false;
  SMADriver: boolean | false;
  CanViewDashBoard: boolean | false;
}

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        backgroundColor: '#0049a3',
        position: 'sticky',
        zIndex: 1297,
        width: '100%',
        maxWidth: '100%',
        top: 0,
        paddingTop: "59px",
        '& header': {
          boxShadow: "none",
        },
    },
    logo: {
        height: '100%',
        textAlign: "center",
        display: "flex",
        justifyContent: 'center',
        alignItems: 'center',
    },
    logoImg: {
        cursor: 'pointer',
        width: 236,
        [theme.breakpoints.only("xs")]: {
            width: "100%",
        },
    },
    navBarElements: {
        fontSize: '18'
    },
    toolbar: {
        padding: 0,
        height: 59,
    },
    leftHandMenu: {
        height: '100%',
        '& .MuiButton-root': {
            minWidth: 0,
            width: "auto",
            fontWeight: "400",
            '& .MuiButton-iconSizeMedium > *:first-child': {
              fontSize: "26px",
            },
        },
    },
    rightHandMenu: {
        height: '100%',
        display: "flex",
        justifyContent: "flex-end",
        alignItems: 'center',
        '& .MuiButton-root': {
            minWidth: 0,
            width: "auto",
            fontWeight: "400",
            '& .MuiSvgIcon-root': {
              height: "1.6em",
              width: "1.6em",
            },
        },
    },
    rightHandMenuButton: {
        color: '#fff',
    }
}));

const getPermissions = (authStore: IAuthState): IUserType => {
  const permissions: IUserType = {
    IsAdmin: authStore?.data?.IsAdmin || false,
    SMADriver: authStore?.data ? (authStore?.data?.role?.indexOf(consts.Permissions.SMADriver) >= 0 ? true : false) : false,
    CanViewDashBoard: authStore?.data?.role
      ?.split(",")
      ?.some((p) => p == consts.Permissions.ViewDashboard || p == consts.Permissions.SMAAdminPatientTransportDashboard)
      ? true
      : false,
  };
  return permissions;
};

function Navbar(props: NavBarProps): JSX.Element {
  const { authStore } = useSelector((state: RootState) => state);
  const classes = useStyles();
  const { navbarContent } = useSelector((state: RootState) => state.appState);
  var permissions = authStore.data?.role.split(",");
  const [enableLeftContent, setEnableLeftContent] = React.useState(true);
  const configureLeftContent = async () => {
    if(permissions){
      if (permissions.length == 1 && ((permissions[0] == consts.Permissions.SMAMakeBooking) || (permissions[0] == consts.Permissions.MakeBooking) || (permissions[0] == ""))) {
        if ((permissions[0] == consts.Permissions.SMAMakeBooking) || (permissions[0] == consts.Permissions.MakeBooking)) {
          setEnableLeftContent(false);
        }
      }
      if (permissions.length == 1 && ((permissions[0] == consts.Permissions.SMASubmitPatientCareRecords) || (permissions[0] == consts.Permissions.SubmitPatientCareRecord) || (permissions[0] == ""))) {
        if ((permissions[0] == consts.Permissions.SMASubmitPatientCareRecords) || (permissions[0] == consts.Permissions.SubmitPatientCareRecord)) {
          setEnableLeftContent(false);
        }
      }
    }
  };
  const leftButtonContent =
    navbarContent != null && navbarContent.leftButtonName && enableLeftContent ? (
      <Fragment>
        <LeftHandMenu />
      </Fragment>
    ) : (
      null
    );
  const userPermissions = getPermissions(authStore);

  const home = (): void => {
    props.history.push("/");
  };
   //clear cache
  const clearCache = async () => {
    try {
      const cacheNames = await caches.keys();
  
      for (const cacheName of cacheNames) {
        const cache = await caches.open(cacheName);
        const keys = await cache.keys();
  
        for (const key of keys) {
          const url = key.url;
  
          if (url.startsWith('/api')) {
            await cache.delete(key);
            console.log(`Cached response for ${url} deleted.`);
          }
        }
      }
    } catch (error) {
      console.error('Error deleting cached responses:', error);
    }
  };
  React.useEffect(() => {
    clearCache();
    configureLeftContent();
  }, []);

  return (
    <div className={classes.root}>
      <AppBar position="fixed" {...(props.noNavBarShadow ? { elevation: 0 } : {})}>
        <Toolbar className={classes.toolbar} variant="dense">
          <Grid container spacing={0} style={{ height: "100%", width: "100%" }}>
            {props.noLogo && props.humberMenu === null ? (
              <Grid xs={12} item>
                <div className={classes.rightHandMenu}>
                  <RightHandMenu />
                </div>
              </Grid>
            ) : (
              <>
                <Grid item xs={3} sm={4}>
                  <div className={classes.leftHandMenu}>
                    {props.humberMenu === null ? null : props.humberMenu || leftButtonContent}
                  </div>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <div className={classes.logo}>
                  {props.noLogo ? null : <NavBarLogoSvg onClick={home} className={classes.logoImg} />}
                  </div>
                </Grid>
                <Grid item xs={3} sm={4}>
                  <div className={classes.rightHandMenu}>
                    <RightHandMenu />
                  </div>
                </Grid>
              </>
            )}
          </Grid>
        </Toolbar>
      </AppBar>
    </div>
  );
}

export default React.memo(withRouter(Navbar));
